// CRM pages: Public Messages inbox + Escort Bookings inbox.
// Both share the same shell with a status filter, list, and detail panel.
import React, { useEffect, useState, useMemo } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Inbox, Calendar, Mail, Phone, AtSign, MapPin, Clock, Image as ImageIcon,
  Trash2, RefreshCw, ExternalLink, CheckCircle2, Archive, AlertOctagon,
  Settings, Check, Plus, X, LoaderCircle,
} from "lucide-react";
import { SERVICE_PRESETS } from "../lib/profilePresets";

const MSG_STATUSES = [
  { v: "new",      l: "New",      color: "#ec4899" },
  { v: "read",     l: "Read",     color: "#06B6D4" },
  { v: "replied",  l: "Replied",  color: "#10b981" },
  { v: "archived", l: "Archived", color: "#71717a" },
  { v: "spam",     l: "Spam",     color: "#dc2626" },
];
const BOOK_STATUSES = [
  { v: "new",        l: "New",        color: "#f97316" },
  { v: "reviewing",  l: "Reviewing",  color: "#06B6D4" },
  { v: "accepted",   l: "Accepted",   color: "#10b981" },
  { v: "declined",   l: "Declined",   color: "#dc2626" },
  { v: "completed",  l: "Completed",  color: "#8b5cf6" },
  { v: "archived",   l: "Archived",   color: "#71717a" },
];

/* ──────────────────── Public Messages ──────────────────── */
export function PublicMessagesPage() {
  return <IntakePage kind="messages" />;
}
export function EscortBookingsPage() {
  return <IntakePage kind="bookings" />;
}

function IntakePage({ kind }) {
  const isMsg = kind === "messages";
  const cfg = isMsg ? {
    title: "Messages",
    overline: "▸ PUBLIC · DIRECT MESSAGES",
    endpoint: "/contact-messages",
    icon: Inbox,
    statuses: MSG_STATUSES,
    accent: "#ec4899",
  } : {
    title: "Bookings",
    overline: "▸ PUBLIC · ESCORT BOOKINGS",
    endpoint: "/escort-bookings",
    icon: Calendar,
    statuses: BOOK_STATUSES,
    accent: "#f97316",
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");  // empty = all
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("requests");
  const [landingConfig, setLandingConfig] = useState(null);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [savingServices, setSavingServices] = useState(false);
  const [customService, setCustomService] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : "";
      const r = await api.get(`${cfg.endpoint}${params}`);
      setItems(r.data || []);
      if (selected) {
        const fresh = (r.data || []).find(x => x.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) {
      toast.error("Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    if (isMsg) return;
    api.get("/api/landing/config")
      .then(({ data }) => setLandingConfig({
        ...data,
        services_offered: Array.isArray(data?.services_offered) ? data.services_offered : [],
      }))
      .catch(error => toast.error(error?.response?.data?.detail || "Could not load Book Me services"))
      .finally(() => setServicesLoaded(true));
  }, [isMsg]);

  const toggleService = (service) => setLandingConfig(current => ({
    ...current,
    services_offered: (current?.services_offered || []).includes(service)
      ? current.services_offered.filter(item => item !== service)
      : [...(current?.services_offered || []), service],
  }));
  const addCustomService = (event) => {
    event.preventDefault();
    const name = customService.trim();
    if (!name) return;
    const services = landingConfig?.services_offered || [];
    if (services.some(service => service.toLowerCase() === name.toLowerCase())) {
      toast.error("That service is already listed");
      return;
    }
    setLandingConfig(current => ({ ...current, services_offered: [...(current?.services_offered || []), name] }));
    setCustomService("");
  };
  const saveServices = async () => {
    if (!landingConfig) return;
    setSavingServices(true);
    try {
      const { data } = await api.put("/api/landing/config", landingConfig);
      setLandingConfig(data);
      toast.success("Book Me services saved");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save Book Me services");
    } finally {
      setSavingServices(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter(i =>
      `${i.name} ${i.email} ${i.twitter_handle} ${i.message || ""} ${i.location || ""} ${i.notes || ""} ${i.interests || ""}`
        .toLowerCase().includes(q));
  }, [items, search]);

  const setStatus = async (id, status) => {
    try {
      const r = await api.patch(`${cfg.endpoint}/${id}`, { status });
      setItems(prev => prev.map(x => x.id === id ? r.data : x));
      if (selected?.id === id) setSelected(r.data);
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error("Update failed"); }
  };
  const setNotes = async (id, admin_notes) => {
    try {
      const r = await api.patch(`${cfg.endpoint}/${id}`, { admin_notes });
      setItems(prev => prev.map(x => x.id === id ? r.data : x));
      if (selected?.id === id) setSelected(r.data);
    } catch { /* */ }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try {
      await api.delete(`${cfg.endpoint}/${id}`);
      setItems(prev => prev.filter(x => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const counts = useMemo(() => {
    const out = { all: items.length };
    cfg.statuses.forEach(s => { out[s.v] = items.filter(i => i.status === s.v).length; });
    return out;
  }, [items, cfg.statuses]);

  return (
    <AppLayout title={cfg.title} overline={cfg.overline}
      actions={
        isMsg || activeTab === "requests" ? (
          <button onClick={load} disabled={loading}
            data-testid={`${kind}-refresh`}
            className="btn-ghost text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        ) : (
          <button type="button" onClick={saveServices} disabled={!servicesLoaded || savingServices || !landingConfig}
            className="btn-primary text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
            {savingServices ? <LoaderCircle size={13} className="animate-spin" /> : <Check size={13} />}
            {savingServices ? "Saving…" : "Save services"}
          </button>
        )
      }>
      <div className="space-y-4" data-testid={`${kind}-page`}>
        {!isMsg && (
          <div className="flex gap-1 border-b" style={{ borderColor: "var(--gio-border)" }} role="tablist" aria-label="Escort booking administration">
            <button type="button" role="tab" aria-selected={activeTab === "requests"} onClick={() => setActiveTab("requests")}
              className={`border-b-2 px-4 py-2 text-sm font-medium transition ${activeTab === "requests" ? "border-orange-400 text-orange-300" : "border-transparent text-zinc-400 hover:text-white"}`}>
              Bookings
            </button>
            <button type="button" role="tab" aria-selected={activeTab === "services"} onClick={() => setActiveTab("services")}
              className={`inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition ${activeTab === "services" ? "border-orange-400 text-orange-300" : "border-transparent text-zinc-400 hover:text-white"}`}>
              <Settings size={14} /> Escort Services
            </button>
          </div>
        )}

        {(isMsg || activeTab === "requests") && <div className="grid lg:grid-cols-[360px_1fr] gap-4 min-h-[60vh]">
        {/* ──── LIST ──── */}
        <section className="bento !p-0 overflow-hidden">
          {/* Filters */}
          <div className="p-3 border-b space-y-2" style={{ borderColor: "var(--gio-border)" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid={`${kind}-search`}
              placeholder="Search…" className="gio-input w-full text-[12px]" />
            <div className="flex flex-wrap gap-1.5">
              <FilterPill label={`All ${counts.all}`} active={!filter} onClick={() => setFilter("")} testid={`${kind}-filter-all`} />
              {cfg.statuses.map(s => (
                <FilterPill key={s.v} label={`${s.l} ${counts[s.v] || 0}`}
                  active={filter === s.v} color={s.color}
                  onClick={() => setFilter(s.v)} testid={`${kind}-filter-${s.v}`} />
              ))}
            </div>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: "70vh" }}>
            {filtered.length === 0 && (
              <div className="text-center py-12 text-[13px]" style={{ color: "var(--gio-muted-3)" }}>
                {loading ? "Loading…" : `No ${cfg.title.toLowerCase()} yet.`}
              </div>
            )}
            {filtered.map((it) => (
              <button key={it.id} onClick={() => setSelected(it)}
                data-testid={`${kind}-row-${it.id}`}
                className="w-full text-left p-3 transition hover:bg-[var(--gio-surface-hi)] border-b"
                style={{
                  background: selected?.id === it.id ? "var(--gio-surface-hi)" : "transparent",
                  borderColor: "var(--gio-border)",
                }}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-[13px] font-bold truncate" style={{ color: "var(--gio-text)" }}>
                    {it.name || "(no name)"}
                  </div>
                  <StatusBadge status={it.status} statuses={cfg.statuses} />
                </div>
                <div className="text-[11px] truncate" style={{ color: "var(--gio-muted-2)" }}>
                  {isMsg
                    ? (it.message || "").slice(0, 80) + (it.message?.length > 80 ? "…" : "")
                    : `${it.service_type} · ${it.duration || "?"} · ${it.location || "(no loc)"}`
                  }
                </div>
                <div className="text-[10px] mt-1 flex items-center gap-2" style={{ color: "var(--gio-muted-3)" }}>
                  <span>{fmtRel(it.created_at)}</span>
                  {isMsg && it.photos?.length > 0 && (
                    <span className="inline-flex items-center gap-0.5"><ImageIcon size={9} />{it.photos.length}</span>
                  )}
                  {it.email && <span>· {it.email}</span>}
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* ──── DETAIL ──── */}
        <section className="bento !p-0 overflow-hidden flex flex-col" data-testid={`${kind}-detail`}>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center p-12 text-center">
              <div>
                <cfg.icon size={48} className="mx-auto mb-3" style={{ color: "var(--gio-muted-3)" }} />
                <div className="text-[14px] font-bold" style={{ color: "var(--gio-text)" }}>Pick an entry</div>
                <div className="text-[12px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
                  Tap a row on the left to see details.
                </div>
              </div>
            </div>
          ) : (
            <DetailPanel item={selected} kind={kind} cfg={cfg}
              onStatus={(s) => setStatus(selected.id, s)}
              onNotes={(n) => setNotes(selected.id, n)}
              onDelete={() => remove(selected.id)} />
          )}
        </section>
        </div>}

        {!isMsg && activeTab === "services" && (
          <section className="space-y-4" aria-label="Book Me escort services">
            <div>
              <h2 className="font-heading text-2xl" style={{ color: "var(--gio-text)" }}>Escort Services</h2>
              <p className="mt-1 text-sm" style={{ color: "var(--gio-muted-2)" }}>Choose the services visitors can select in the public Book Me form.</p>
            </div>
            {!servicesLoaded ? (
              <div className="py-12 text-center text-sm" style={{ color: "var(--gio-muted-2)" }}><LoaderCircle size={18} className="mx-auto mb-2 animate-spin" />Loading services…</div>
            ) : (
              <>
                <section className="bento space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold" style={{ color: "var(--gio-text)" }}>Available services</h3>
                      <p className="mt-1 text-xs" style={{ color: "var(--gio-muted-2)" }}>Selected services appear as choices in Book Me.</p>
                    </div>
                    <span className="shrink-0 border px-2 py-1 text-xs" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>
                      {(landingConfig?.services_offered || []).length} selected
                    </span>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {SERVICE_PRESETS.map(service => {
                      const selectedService = (landingConfig?.services_offered || []).includes(service);
                      return (
                        <button key={service} type="button" aria-pressed={selectedService} onClick={() => toggleService(service)}
                          className={`flex min-h-11 items-center justify-between gap-3 border px-3 py-2 text-left text-sm transition ${selectedService ? "border-orange-400/70 bg-orange-400/10 text-white" : "text-zinc-300 hover:text-white"}`}
                          style={!selectedService ? { borderColor: "var(--gio-border)", background: "var(--gio-surface)" } : undefined}>
                          <span>{service}</span>
                          {selectedService && <Check size={15} className="shrink-0 text-orange-300" />}
                        </button>
                      );
                    })}
                  </div>
                </section>
                <section className="bento space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: "var(--gio-text)" }}>Custom services</h3>
                    <p className="mt-1 text-xs" style={{ color: "var(--gio-muted-2)" }}>Add a service label not covered by the presets.</p>
                  </div>
                  <form onSubmit={addCustomService} className="flex flex-col gap-2 sm:flex-row">
                    <input value={customService} onChange={event => setCustomService(event.target.value)} maxLength={80}
                      aria-label="Custom service name" placeholder="Enter a service name" className="gio-input min-w-0 flex-1" />
                    <button type="submit" disabled={!customService.trim()} className="btn-ghost inline-flex min-h-10 items-center justify-center gap-2 disabled:opacity-50">
                      <Plus size={14} /> Add service
                    </button>
                  </form>
                  <div className="flex flex-wrap gap-2">
                    {(landingConfig?.services_offered || []).filter(service => !SERVICE_PRESETS.includes(service)).map(service => (
                      <span key={service} className="inline-flex items-center gap-2 border border-orange-400/40 bg-orange-400/10 py-1 pl-3 pr-1 text-sm text-orange-100">
                        {service}
                        <button type="button" onClick={() => toggleService(service)} aria-label={`Remove ${service}`} className="p-1 hover:bg-orange-400/20"><X size={14} /></button>
                      </span>
                    ))}
                  </div>
                </section>
                <p className="border-l-2 border-emerald-500 px-3 py-2 text-sm" style={{ color: "var(--gio-muted-2)" }} aria-live="polite">
                  {landingConfig?.services_offered?.length
                    ? `${landingConfig.services_offered.length} service${landingConfig.services_offered.length === 1 ? "" : "s"} will be shown in Book Me.`
                    : "No services selected. The additional service choices will be hidden from Book Me."}
                </p>
              </>
            )}
          </section>
        )}
      </div>
    </AppLayout>
  );
}

function FilterPill({ label, active, color, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider border rounded-full transition"
      style={{
        borderColor: active ? (color || "var(--gio-primary)") : "var(--gio-border)",
        color: active ? (color || "var(--gio-primary)") : "var(--gio-muted-2)",
        background: active ? `${color || "var(--gio-primary)"}15` : "transparent",
      }}>{label}</button>
  );
}

function StatusBadge({ status, statuses }) {
  const m = statuses.find(s => s.v === status) || statuses[0];
  return (
    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
      style={{ background: `${m.color}22`, color: m.color }}>{m.l}</span>
  );
}

function DetailPanel({ item, kind, cfg, onStatus, onNotes, onDelete }) {
  const isMsg = kind === "messages";
  const [notes, setNotesLocal] = useState(item.admin_notes || "");
  useEffect(() => { setNotesLocal(item.admin_notes || ""); }, [item.id]); // eslint-disable-line
  const saveNotes = () => {
    if (notes !== (item.admin_notes || "")) onNotes(notes);
  };

  return (
    <>
      <div className="p-4 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--gio-border)" }}>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-2xl truncate" style={{ color: "var(--gio-text)" }}>{item.name || "(no name)"}</div>
          <div className="text-[12px] mt-1 flex flex-wrap gap-3" style={{ color: "var(--gio-muted-2)" }}>
            {item.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {item.email}</span>}
            {item.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {item.phone}</span>}
            {item.twitter_handle && (
              <a href={`https://x.com/${item.twitter_handle.replace(/^@/, "")}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 hover:underline">
                <AtSign size={11} /> @{item.twitter_handle.replace(/^@/, "")} <ExternalLink size={10} />
              </a>
            )}
          </div>
          <div className="text-[11px] mt-1" style={{ color: "var(--gio-muted-3)" }}>
            {new Date(item.created_at).toLocaleString()} · IP {item.ip}
          </div>
        </div>
        <button onClick={onDelete} data-testid={`${kind}-delete`}
          className="text-[var(--gio-muted)] hover:text-red-500"><Trash2 size={16} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Status buttons */}
        <div className="flex flex-wrap gap-1.5">
          {cfg.statuses.map(s => (
            <button key={s.v} onClick={() => onStatus(s.v)}
              data-testid={`${kind}-set-${s.v}`}
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border rounded-full transition"
              style={{
                borderColor: item.status === s.v ? s.color : "var(--gio-border)",
                color: item.status === s.v ? s.color : "var(--gio-muted-2)",
                background: item.status === s.v ? `${s.color}15` : "transparent",
              }}>{s.l}</button>
          ))}
        </div>

        {isMsg ? (
          <>
            <Section title="Message">
              <pre className="whitespace-pre-wrap font-sans text-[14px] leading-relaxed" style={{ color: "var(--gio-text)" }}>
{item.message}
              </pre>
            </Section>
            {item.photos?.length > 0 && (
              <Section title={`Attached photos (${item.photos.length})`}>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {item.photos.map((u, i) => (
                    <a key={i} href={u} target="_blank" rel="noreferrer"
                      data-testid={`${kind}-photo-${i}`}
                      className="aspect-square rounded-lg overflow-hidden border block"
                      style={{ borderColor: "var(--gio-border)" }}>
                      <img src={u} alt="" className="w-full h-full object-cover hover:scale-110 transition" />
                    </a>
                  ))}
                </div>
              </Section>
            )}
          </>
        ) : (
          <>
            <Section title="Booking details">
              <dl className="grid grid-cols-2 gap-2 text-[13px]">
                <Row k="Service" v={item.service_type} />
                <Row k="Duration" v={item.duration} />
                <Row k="Type" v={item.visit_kind} />
                <Row k="Location" v={item.location} icon={<MapPin size={11} />} />
                <Row k="Date" v={item.preferred_date} icon={<Calendar size={11} />} />
                <Row k="Time" v={item.preferred_time} icon={<Clock size={11} />} />
                <Row k="Budget" v={item.budget} />
              </dl>
            </Section>
            {item.interests && (
              <Section title="Interests / vibe">
                <p className="whitespace-pre-wrap text-[14px]" style={{ color: "var(--gio-text)" }}>{item.interests}</p>
              </Section>
            )}
            {item.notes && (
              <Section title="Notes">
                <p className="whitespace-pre-wrap text-[14px]" style={{ color: "var(--gio-text)" }}>{item.notes}</p>
              </Section>
            )}
          </>
        )}

        <Section title="My notes (private)">
          <textarea value={notes} onChange={(e) => setNotesLocal(e.target.value)}
            onBlur={saveNotes}
            data-testid={`${kind}-notes`}
            placeholder="Add private notes about this entry…"
            rows={3} className="gio-input w-full text-[13px]" />
        </Section>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--gio-muted-2)" }}>▸ {title}</div>
      {children}
    </div>
  );
}
function Row({ k, v, icon }) {
  return (
    <>
      <dt className="text-[11px] uppercase tracking-wider" style={{ color: "var(--gio-muted-2)" }}>{k}</dt>
      <dd style={{ color: "var(--gio-text)" }} className="inline-flex items-center gap-1">{icon}{v || "—"}</dd>
    </>
  );
}
function fmtRel(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return "now";
    if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
    if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}
